import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

/**
 * Validates that this control's number is greater than or equal to a sibling
 * control's number. Mirrors backend rules that compare two quantity fields, so
 * the user is stopped inline instead of by a 422 after submitting.
 *
 * A blank value on either side passes, matching the backend checks, which skip
 * the comparison when either number is nil.
 *
 * The sibling's value changing does not re-run this validator on its own, so the
 * caller must revalidate this control when the sibling changes.
 */
export function validateGreaterThanOrEqual(siblingControlName: string): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = toNumber(control.value);
    const siblingValue = toNumber(control.parent?.get(siblingControlName)?.value);
    if (value === null || siblingValue === null || value >= siblingValue) {
      return null;
    }
    return { lessThanSibling: { siblingControlName, minimum: siblingValue, actual: value } };
  };
}

// Masked inputs hand back strings, and an empty/whitespace string is "not set"
// rather than 0 — Number('') is 0, which would wrongly fail the comparison.
function toNumber(value: unknown): number | null {
  if (value === null || value === undefined || `${value}`.trim() === '') {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}
